import React from 'react';
import { useNavigate } from 'react-router-dom';
import { formatDateRelative } from '../utils/helpers';
import { notificationRoute } from '../utils/notificationRoute';
import './NotificationsModal.css';

// Three tabs PARTITION the user's notifications (server-derived, task-mirror
// doctrine — never a stored "archived" stamp):
//   Unread   — not seen yet AND still waiting on somebody.
//   Read     — seen, but the underlying request is still open.
//   Archived — handled by others (referent resolved), or an FYI you've read.
// Rows migrate between tabs on their own: claim a task elsewhere and its
// alert archives; release it and the alert comes back.
function NotificationsModal({
  notifications,
  counts,
  tab,
  onTabChange,
  onLoadMore,
  onClose,
  onMarkAsRead,
  onMarkAllAsRead,
  onDelete,
  loading
}) {
  const navigate = useNavigate();

  // Using centralized formatDateRelative from helpers.js
  const formatDate = formatDateRelative;

  const TABS = [
    { id: 'unread', label: 'Unread' },
    { id: 'read', label: 'Read' },
    { id: 'archived', label: 'Archived' },
  ];
  const EMPTY_TEXT = {
    unread: 'Nothing waiting on you',
    read: 'Nothing read and still open',
    archived: 'Nothing archived yet',
  };
  const hasMore = counts && notifications.length < (counts[tab] || 0);

  const handleNotificationClick = (notification) => {
    // Mark as read (NOT delete) — fire-and-forget. This must NEVER block the
    // navigation below: if the mark-read request is slow or fails, the click
    // should still take the user to the action page.
    if (!notification.is_read && onMarkAsRead) {
      Promise.resolve(onMarkAsRead(notification.id)).catch(() => {});
    }
    onClose();

    // Routing decision lives in notificationRoute (shared with the web-push
    // landing handler in Dashboard) so both entry points always agree.
    const destination = notificationRoute(notification);
    if (destination) {
      navigate(destination.path, { state: destination.state });
    }
  };

  const handleDelete = async (e, notificationId) => {
    e.stopPropagation();
    await onDelete(notificationId);
  };

  return (
    <div className="modal-overlay">
      <div className="notifications-modal" onClick={e => e.stopPropagation()}>
        <div className="notifications-header">
          <h2>Notifications</h2>
          <div className="notifications-actions">
            {tab === 'unread' && (counts?.unread || 0) > 0 && (
              <button
                className="mark-all-read-btn"
                onClick={onMarkAllAsRead}
              >
                Mark all as read
              </button>
            )}
            <button className="close-btn" onClick={onClose}>×</button>
          </div>
        </div>

        <div className="notifications-tabs" style={{ display: 'flex', gap: 4, padding: '8px 16px 0' }}>
          {TABS.map(t => (
            <button
              key={t.id}
              type="button"
              onClick={() => onTabChange(t.id)}
              style={{
                padding: '6px 14px',
                border: 'none',
                borderBottom: tab === t.id ? '2px solid #2563eb' : '2px solid transparent',
                background: 'none',
                cursor: 'pointer',
                fontWeight: tab === t.id ? 600 : 400,
                color: tab === t.id ? '#1f2937' : '#6b7280',
              }}
            >
              {t.label}{counts ? ` (${counts[t.id] || 0})` : ''}
            </button>
          ))}
        </div>

        <div className="notifications-content">
          {loading ? (
            <div className="notifications-loading">
              <div className="spinner"></div>
              <p>Loading notifications...</p>
            </div>
          ) : notifications.length === 0 ? (
            <div className="no-notifications">
              <span className="no-notifications-icon"></span>
              <p>{EMPTY_TEXT[tab] || 'No notifications yet'}</p>
              {tab === 'unread' && (
                <p className="no-notifications-hint">
                  New alerts land here; once somebody handles the underlying request they move to Archived on their own.
                </p>
              )}
            </div>
          ) : (
            <div className="notifications-list">
              {notifications.map(notification => (
                <div
                  key={notification.id}
                  className={`notification-item ${!notification.is_read && tab !== 'archived' ? 'unread' : ''}`}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div className="notification-icon">
                    {notification.notification_type === 'payment_completed' ? '💸' :
                     notification.notification_type === 'payment_received_recorded' ? '💰' :
                     notification.notification_type === 'payment_received_reverted' ? '↩️' :
                     notification.notification_type === 'platform_order_paid' ? '✅' :
                     notification.notification_type === 'platform_order_released' ? '🔄' :
                     notification.notification_type === 'platform_order_completed' ? '🎉' :
                     notification.notification_type?.startsWith('platform_order_') ? '📦' :
                     notification.notification_type?.startsWith('customer_takeover_') ? '👤' :
                     notification.notification_type?.startsWith('customer_vip_') ? '⭐' :
                     notification.notification_type === 'task_new' ? '🆕' :
                     /* Creator-lifecycle types (2026-07-30): who took your task,
                        where it stands, and when it went backwards. */
                     notification.notification_type === 'task_claimed_yours' ? '🙋' :
                     notification.notification_type === 'task_progress' ? '⏳' :
                     notification.notification_type === 'task_released' ? '🔄' :
                     notification.notification_type === 'task_reverted' ? '↩️' :
                     (notification.notification_type === 'system' && (notification.title || '').toLowerCase().includes('takeover')) ? '👤' : '📋'}
                  </div>
                  <div className="notification-body">
                    <div className="notification-title">{notification.title}</div>
                    <div className="notification-message">{notification.message}</div>
                    <div className="notification-meta">
                      <span className="notification-time">{formatDate(notification.created_at)}</span>
                      {notification.order_id && (
                        <span className="notification-order">Order #{notification.order_id}</span>
                      )}
                    </div>
                  </div>
                  <div className="notification-actions">
                    {!notification.is_read && tab !== 'archived' && (
                      <span className="unread-dot" title="Unread"></span>
                    )}
                    <button
                      className="delete-notification-btn"
                      onClick={(e) => handleDelete(e, notification.id)}
                      title="Delete notification"
                    >
                      ×
                    </button>
                  </div>
                </div>
              ))}
              {hasMore && (
                <button
                  type="button"
                  onClick={onLoadMore}
                  style={{ display: 'block', width: '100%', padding: '10px', border: 'none', background: '#f6f8fa', cursor: 'pointer', color: '#2563eb', fontWeight: 500 }}
                >
                  Load more ({(counts[tab] || 0) - notifications.length} older)
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default NotificationsModal;

